import React, { useLayoutEffect, useRef, useEffect } from 'react';
import { Button, Flex, Tooltip } from 'antd';
import { FileDownload } from '../../../assets/images';
import { CustomParagraph } from '../../../components';
import { SingleShipmentDataExtended } from '@/apis/types';
import { Custody } from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import '../../../pages/Shipments/MapExternalView/Style.css';
import { getSystemFileUploadLabel } from './getSystemFileUploadLabel';

interface FormattedTime {
  full?: string;
  relative?: string;
}

interface TimelineItemExternalProps {
  isSharePage?: boolean;
  custody: Custody;
  setShowFull: (updater: (prev: boolean) => boolean) => void;
  showFull: boolean;
  getFormattedTime: (timestamp?: string, timezone?: string) => FormattedTime;
  downloadFile: (file?: string, fileName?: string) => void;
  setHalfHeight: (custodyId: string, height: number) => void;
  shipment?: Partial<SingleShipmentDataExtended>;
}

/**
 * Component for displaying external timeline items in shipment custody chain
 *
 * @param custody - Custody record with action, comment, and file information
 * @param setShowFull - Function to toggle between full and relative time display
 * @param showFull - Boolean indicating whether to show full timestamp
 * @param getFormattedTime - Function to format timestamp into full and relative formats
 * @param downloadFile - Function to download attached files
 * @param setHalfHeight - Function to set the half height for timeline positioning
 * @param shipment - Shipment data containing custody chain
 */
const SENSOR_HANDLER_LABELS: Record<string, string> = {
  tive: 'Tive',
  onasset: 'OnAsset',
  roambee: 'Roambee',
  elpro: 'Elpro',
  tempmate: 'Tempmate',
  trackonomy: 'Trackonomy',
};

const TimelineItemSharedPage: React.FC<TimelineItemExternalProps> = ({
  isSharePage = false,
  custody,
  setShowFull,
  showFull,
  getFormattedTime,
  downloadFile,
  setHalfHeight,
  shipment,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const repliesRef = useRef<HTMLDivElement>(null);
  const systemFileLabel =
    custody?.type === CoCEventType.FileUpload && custody?.created_by === 6
      ? getSystemFileUploadLabel(custody?.file_name)
      : null;

  const setHalfHeightRef = useRef(setHalfHeight);
  useEffect(() => {
    setHalfHeightRef.current = setHalfHeight;
  }, [setHalfHeight]);

  useLayoutEffect(() => {
    const measureHeights = () => {
      if (!parentRef.current) return;

      const parentHeight = parentRef.current.getBoundingClientRect().height;
      const repliesHeight = repliesRef.current
        ? repliesRef.current.getBoundingClientRect().height
        : 0;

      const totalHeight = parentHeight + repliesHeight;
      setHalfHeightRef.current?.(custody.id, totalHeight / 2);
    };

    const rafId = requestAnimationFrame(measureHeights);
    return () => cancelAnimationFrame(rafId);
  }, [custody?.id, shipment?.custody]);

  return (
    <div>
      <div
        ref={parentRef}
        className='flex-center btw y w-full relative gap-x-1'
      >
        <Flex align='center' gap={12}>
          <p
            className={`zl-para-12 ${custody?.type === CoCEventType.FileUpload && 'word-break-all'}`}
          >
            {systemFileLabel
              ? systemFileLabel
              : SENSOR_HANDLER_LABELS[custody?.handler ?? '']
                ? `${SENSOR_HANDLER_LABELS[custody?.handler ?? '']} ${(custody?.action ?? '').replace(/^Sensor\s+/i, '')}`
                : custody?.action}
          </p>
          {custody?.file && custody?.type === CoCEventType.FileUpload && (
            <Button
              className='zl-btn icon-btn-26 radius-full'
              onClick={() => downloadFile(custody?.file, custody?.file_name)}
            >
              <img src={FileDownload} width={14} alt='Download file'></img>
            </Button>
          )}
        </Flex>

        <span
          onClick={() => setShowFull((prev) => !prev)}
          style={{ cursor: 'pointer' }}
        >
          {isSharePage && shipment?.tooltip ? (
            <Tooltip
              arrow={false}
              title='Location timezone unavailable; shown in UTC'
              overlayClassName='custom-timezone-tooltip'
              overlayInnerStyle={{
                textAlign: 'center',
                width: '177px',
                padding: '8px 12px',
                fontSize: '12px',
                backgroundColor: 'var(--surface-container)',
                color: 'var(--text-primary)',

                boxShadow: '1px 1px 4px rgba(0, 0, 0, 0.25)',
              }}
              overlayStyle={{
                backgroundColor: 'var(--surface-container)',
                borderRadius: 4,
              }}
              color='#FFFFFF'
            >
              <div
                style={{
                  padding: '2px 4px',
                  display: 'inline-block',
                }}
              >
                <CustomParagraph
                  text={
                    showFull
                      ? getFormattedTime(custody?.timestamp, custody?.timezone)
                          ?.full
                      : getFormattedTime(custody?.timestamp, custody?.timezone)
                          ?.relative
                  }
                  fontSize='12px'
                  fontWeight={400}
                  lineHeight='16px'
                  color='var(--text-primary)'
                  marginBottom='0'
                  style={{ whiteSpace: 'nowrap' }}
                />
              </div>
            </Tooltip>
          ) : (
            <div
              style={{
                padding: '2px 4px',
                display: 'inline-block',
              }}
            >
              <CustomParagraph
                text={
                  showFull
                    ? getFormattedTime(custody?.timestamp, custody?.timezone)
                        ?.full
                    : getFormattedTime(custody?.timestamp, custody?.timezone)
                        ?.relative
                }
                fontSize='12px'
                fontWeight={400}
                lineHeight='16px'
                color='var(--text-primary)'
                marginBottom='0'
                style={{ whiteSpace: 'nowrap' }}
              />
            </div>
          )}
        </span>
      </div>
      <>
        <CustomParagraph
          text={String(custody?.comment || '')}
          fontSize='12px'
          fontWeight={400}
          lineHeight='16px'
          color='var(--text-light)'
          marginBottom='4px'
        />
        {custody?.location_is_plain_text ? (
          <div ref={repliesRef} className='zl-para-12 coc-comment-editor'>
            {custody?.location ?? ''}
          </div>
        ) : (
          <div
            ref={repliesRef}
            className='zl-para-12 coc-comment-editor'
            dangerouslySetInnerHTML={{
              __html: custody?.location,
            }}
          />
        )}
      </>
    </div>
  );
};

export default TimelineItemSharedPage;
